'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Contact from '@/components/sections/Contact';
import { Clock, Calendar, Zap, ChevronDown, HelpCircle } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const responseInfo = [
  {
    icon: Clock,
    title: '24 Hour Response',
    desc: 'I reply to every serious inquiry within one business day.',
  },
  {
    icon: Calendar,
    title: 'Monday — Friday',
    desc: 'Working hours: 9 AM – 7 PM (GMT+6, Dhaka). Weekends off.',
  },
  {
    icon: Zap,
    title: 'Quick Start',
    desc: 'Most projects kick off within a week of the first conversation.',
  },
];

const faqs = [
  {
    q: 'What services do you offer?',
    a: 'I build full-stack web applications, landing pages, dashboards, REST/GraphQL APIs, and provide technical consulting. My main stacks are Next.js, React, Laravel and Node.js.',
  },
  {
    q: 'How long does a typical project take?',
    a: 'A focused landing page usually ships in 1–2 weeks. A full product with authentication, dashboards and integrations typically takes 4–10 weeks depending on scope.',
  },
  {
    q: 'Do you work with international clients?',
    a: 'Absolutely. I collaborate with clients worldwide using Slack, Notion, and weekly video syncs. I adjust my hours to overlap with your timezone when needed.',
  },
  {
    q: 'What is your pricing model?',
    a: 'I offer fixed-price quotes for well-defined scopes and hourly rates for ongoing work. After a free 30-minute call I send a written proposal with a transparent breakdown.',
  },
  {
    q: 'Do you provide support after launch?',
    a: 'Yes. Every project includes 30 days of free post-launch support, and I offer monthly maintenance retainers for bug fixes, updates and new features.',
  },
];

export default function ContactPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="contact-page">
      <Contact />

      {/* Response Time */}
      <section className="contact-page-response">
        <div className="section-container">
          <motion.div
            className="section-heading"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <span className="section-label">Response Time</span>
            <h2 className="section-title">What to expect after you hit send</h2>
            <p className="section-subtitle">
              Here&apos;s how I handle every incoming message — so you know what to expect.
            </p>
          </motion.div>

          <div className="contact-page-response-grid">
            {responseInfo.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  className="contact-page-response-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="contact-page-response-icon">
                    <Icon size={22} />
                  </div>
                  <h3 className="contact-page-response-title">{item.title}</h3>
                  <p className="contact-page-response-desc">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="contact-page-faq">
        <div className="section-container">
          <motion.div
            className="section-heading"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <span className="section-label">
              <HelpCircle size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
              FAQ
            </span>
            <h2 className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle">
              Quick answers to the things clients ask me most often.
            </p>
          </motion.div>

          <div className="contact-page-faq-list">
            {faqs.map((faq, i) => {
              const isOpen = openIndex === i;
              return (
                <motion.div
                  key={faq.q}
                  className={`contact-page-faq-item ${isOpen ? 'contact-page-faq-item-open' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                >
                  <button
                    type="button"
                    className="contact-page-faq-trigger"
                    onClick={() => toggle(i)}
                    aria-expanded={isOpen}
                  >
                    <span className="contact-page-faq-q">{faq.q}</span>
                    <motion.span
                      className="contact-page-faq-chevron"
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <ChevronDown size={18} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="content"
                        className="contact-page-faq-content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                      >
                        <p className="contact-page-faq-a">{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
